import React, { FC, useState, useCallback } from 'react';
import Spinner from '@johnlewispartnership/wtr-ingredients/ingredients/Spinner/index.js';
import Button from '@johnlewispartnership/wtr-ingredients/ingredients/Button/index.js';
import { useCommonModal } from 'components/CommonModal';
import { urls } from 'constants/urls';
import { navigateTo } from 'utils/location';
import { useSlotContext } from 'contexts/Slot';
import { dayjs, FORMAT, format } from 'utils/date';
import unexpectedErrorModal from 'constants/modals/unexpectedErrorModal';
import { CommonModalProps } from 'components/CommonModal/context';
import { PlaceOrder409Error } from 'services/orderOrchestration/types';
import useInstantCheckout from './useInstantCheckout';

import styles from './InstantCheckoutModalContent.module.scss';

export type InstantCheckoutSlotExpiredErrorModalContentProps = {
  location: string;
  getInstantCheckoutErrorModal: (error: PlaceOrder409Error, location?: string) => CommonModalProps;
};

const InstantCheckoutSlotExpiredErrorModalContent: FC<
  InstantCheckoutSlotExpiredErrorModalContentProps
> = ({ location, getInstantCheckoutErrorModal }) => {
  const { openModal } = useCommonModal();
  const [loading, setLoading] = useState(false);
  const { slotContext, cancelSlot } = useSlotContext();
  const { handleInstantCheckout } = useInstantCheckout(location, getInstantCheckoutErrorModal);

  const dayOfWeek = format(dayjs(slotContext.details?.startDateTime).tz(), FORMAT.DAY_OF_WEEK);
  const monthDate = format(dayjs(slotContext.details?.startDateTime).tz(), FORMAT.LONG_MONTH_DAY);
  const startTime = format(dayjs(slotContext.details?.startDateTime).tz(), FORMAT.PAD_HOUR_AMPM);
  const endTime = format(dayjs(slotContext.details?.endDateTime).tz(), FORMAT.PAD_HOUR_AMPM);

  const handleRetryInstantCheckout = useCallback(async () => {
    setLoading(true);
    try {
      await cancelSlot();
      // handleInstantCheckout will either redirect if it's successful or show an error modal
      await handleInstantCheckout();
    } catch {
      setLoading(false);
      openModal(unexpectedErrorModal);
    }
  }, [cancelSlot, handleInstantCheckout, openModal]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.content} aria-hidden={loading}>
        You will need to re-book if you want a new slot. We&apos;ve saved your previous slot. Your
        order is scheduled for: {dayOfWeek}, {monthDate} at {startTime}-{endTime}
      </div>
      <div className={styles.buttons} aria-hidden={loading}>
        <Button
          label="Proceed with previous slot"
          theme="secondary"
          disabled={loading}
          onClick={handleRetryInstantCheckout}
        />
        <Button
          label="Choose a new slot"
          theme="primary"
          disabled={loading}
          onClick={() => {
            navigateTo(urls.serviceSelection);
          }}
        />
      </div>
      {loading && (
        <div className={styles.spinner}>
          <Spinner isActive ariaTextActive="Loading... Waiting for instant checkout to finish" />
        </div>
      )}
    </div>
  );
};

export default InstantCheckoutSlotExpiredErrorModalContent;
